import { QueryConsumer } from 'app/components/Contexts';
import { DisplayRole, STUDENT, TEACHER } from 'app/constants/DisplayRoles';
import { TEACH_LESSON_ABILITY } from 'app/constants/enums/Ability';
import RestrictContent from 'app/containers/auth/RestrictContent';
import { getWithDefault } from 'app/utils/fpTs';
import { parseBoolean } from 'app/utils/parse';
import React, { createContext, PureComponent, ReactNode, useContext } from 'react';

/**
 * This provides the display role. Role is either teacher or student. This
 * controls whether certain sections of a lesson are visible or not.
 */
export interface IDisplayRoleContext {
  role: DisplayRole | null;
  override: (role: DisplayRole) => void;
}

export const DisplayRoleContext = createContext<IDisplayRoleContext>({
  role: null,
  override: () => {},
});

export const useDisplayRole = () => useContext(DisplayRoleContext);

interface IProps {
  initialRole?: DisplayRole;
}

interface IState {
  role: DisplayRole;
}

export class OverridableDisplayRoleProvider extends PureComponent<IProps, IState> {
  public constructor(props: IProps) {
    super(props);
    this.state = {
      role: props.initialRole || STUDENT,
    };
  }

  public render() {
    const value = {
      role: this.state.role,
      override: this.override,
    };
    return (
      <DisplayRoleContext.Provider value={value}>
        {this.props.children}
      </DisplayRoleContext.Provider>
    );
  }

  private override = (role: DisplayRole) => {
    this.setState({ role });
  };
}

interface IChildProps {
  role: DisplayRole;
  override: (role: DisplayRole) => void;
}

interface IConsumerProps {
  children: (context: IChildProps) => ReactNode;
}

export function DisplayRoleConsumer({ children }: IConsumerProps) {
  return (
    <QueryConsumer>{({ teacher }) =>
      <RestrictContent allow={[TEACH_LESSON_ABILITY]}>{(isRestricted) => {
        const teacherOverride = getWithDefault(parseBoolean(teacher), false);
        const userRole = (!isRestricted || teacherOverride) ? TEACHER : STUDENT;
        return (
          <DisplayRoleContext.Consumer>{({ role, override }) =>
            children({
              role: role || userRole,
              override,
            })
          }</DisplayRoleContext.Consumer>
        );
      }}</RestrictContent>
    }</QueryConsumer>
  );
}
